import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import toast from "react-hot-toast";
import {
  PageHeader,
  Card,
  SectionTitle,
  Badge,
  Button,
  Field,
  Input,
  Select,
  Textarea,
  Sheet,
  Chip,
  ChipRow,
  SkeletonList,
  EmptyState,
  formatMoney,
  formatDate,
} from "../components/ui";
import { PlusIcon } from "../components/icons";

const TYPE_META = {
  fuel: { icon: "⛽", label: "Fuel", tone: "amber", ring: "border-amber-300 bg-amber-50 text-amber-900" },
  food: { icon: "🍱", label: "Food", tone: "green", ring: "border-brand-300 bg-brand-50 text-brand-900" },
};

const STATUS_TONE = { pending: "amber", approved: "green", rejected: "red" };

/* ─────────────────────────────────────────────────────────────
   Form
───────────────────────────────────────────────────────────── */

function ExpenseForm({ formId, onSave, sites, vehicles, defaultType, onSavingChange }) {
  const [form, setForm] = useState({
    siteId: sites[0]?._id || "",
    type: defaultType || "fuel",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    vehicleId: "",
    fuelLitres: "",
    fuelRate: "",
    receiptNote: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isFuel = form.type === "fuel";

  // Litres × rate → amount, so the crew never does mental maths at a pump.
  useEffect(() => {
    if (isFuel && form.fuelLitres && form.fuelRate) {
      const total = parseFloat(form.fuelLitres) * parseFloat(form.fuelRate);
      if (!Number.isNaN(total)) set("amount", total.toFixed(2));
    }
  }, [form.fuelLitres, form.fuelRate, isFuel]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Enter a valid amount");
    onSavingChange(true);
    try {
      await api.post("/expenses/crew", form);
      toast.success("Expense submitted ✅");
      onSave();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not submit expense");
    } finally {
      onSavingChange(false);
    }
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-5">
      {/* Type toggle */}
      <fieldset>
        <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
          Type
        </legend>
        <div className="grid grid-cols-2 gap-2.5">
          {Object.entries(TYPE_META).map(([type, meta]) => {
            const active = form.type === type;
            return (
              <button
                key={type}
                type="button"
                aria-pressed={active}
                onClick={() => set("type", type)}
                className={`flex min-h-[56px] items-center justify-center gap-2.5 rounded-xl border-2 font-display text-sm font-semibold transition-colors ${
                  active ? meta.ring : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                <span className="text-xl" aria-hidden="true">{meta.icon}</span>
                {meta.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      {sites.length > 1 && (
        <Field label="Site" htmlFor="exp-site">
          <Select id="exp-site" value={form.siteId} onChange={(e) => set("siteId", e.target.value)}>
            <option value="">No specific site</option>
            {sites.map((s) => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </Select>
        </Field>
      )}

      {isFuel && (
        <div className="space-y-3.5 rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">
            ⛽ Fuel details
          </p>
          {vehicles.length > 0 && (
            <Select
              value={form.vehicleId}
              onChange={(e) => set("vehicleId", e.target.value)}
              aria-label="Vehicle"
            >
              <option value="">Select vehicle (optional)</option>
              {vehicles.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.make} {v.model} — {v.regNumber}
                </option>
              ))}
            </Select>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Litres" htmlFor="exp-litres">
              <Input
                id="exp-litres"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="40"
                value={form.fuelLitres}
                onChange={(e) => set("fuelLitres", e.target.value)}
              />
            </Field>
            <Field label="Rate / L (₹)" htmlFor="exp-rate">
              <Input
                id="exp-rate"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="102"
                value={form.fuelRate}
                onChange={(e) => set("fuelRate", e.target.value)}
              />
            </Field>
          </div>
        </div>
      )}

      <Field label="Amount (₹)" required htmlFor="exp-amount">
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-display text-xl font-bold text-slate-400"
          >
            ₹
          </span>
          <input
            id="exp-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            required
            placeholder="0.00"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            className="h-16 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 font-display text-2xl font-bold text-slate-900 placeholder-slate-300 tabular focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/15"
          />
        </div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Date" htmlFor="exp-date">
          <Input
            id="exp-date"
            type="date"
            max={new Date().toISOString().split("T")[0]}
            value={form.date}
            onChange={(e) => set("date", e.target.value)}
          />
        </Field>
        <Field label="Receipt ref" htmlFor="exp-receipt">
          <Input
            id="exp-receipt"
            type="text"
            placeholder="Optional"
            value={form.receiptNote}
            onChange={(e) => set("receiptNote", e.target.value)}
          />
        </Field>
      </div>

      <Field label="Description" htmlFor="exp-desc">
        <Textarea
          id="exp-desc"
          rows={2}
          placeholder="Brief description…"
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
        />
      </Field>
    </form>
  );
}

/* ─────────────────────────────────────────────────────────────
   Page
───────────────────────────────────────────────────────────── */

export default function Expenses() {
  const { type: defaultType } = useParams();
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState([]);
  const [sites, setSites] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(!!defaultType);
  const [saving, setSaving] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [filterType, setFilterType] = useState("");

  const fetchExpenses = useCallback(async () => {
    try {
      const p = new URLSearchParams({ month });
      if (filterType) p.set("type", filterType);
      const { data } = await api.get(`/expenses/crew?${p}`);
      setExpenses(data.data || []);
    } catch {
      setExpenses([]);
    }
  }, [month, filterType]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/sites/crew").catch(() => ({ data: { data: [] } })),
      api.get("/vehicles").catch(() => ({ data: { data: [] } })),
      fetchExpenses(),
    ])
      .then(([s, v]) => {
        setSites(s.data.data || []);
        setVehicles(v.data.data || []);
      })
      .finally(() => setLoading(false));
  }, [fetchExpenses]);

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  const approved = expenses
    .filter((e) => e.status === "approved")
    .reduce((s, e) => s + (e.amount || 0), 0);
  const pending = expenses.filter((e) => e.status === "pending").length;

  const openForm = (type) => {
    setShowForm(true);
    navigate(type ? `/expenses/new/${type}` : "/expenses/new", { replace: true });
  };
  const closeForm = () => {
    setShowForm(false);
    navigate("/expenses", { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Expenses"
        subtitle="Fuel & food claims"
        action={
          <Button onClick={() => openForm(null)} size="md">
            <PlusIcon className="h-4 w-4" />
            Add
          </Button>
        }
      />

      {/* Summary — the whole list, at a glance */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Total", val: formatMoney(total), cls: "text-slate-900" },
          { label: "Approved", val: formatMoney(approved), cls: "text-brand-800" },
          { label: "Pending", val: pending, cls: pending ? "text-amber-700" : "text-slate-700" },
        ].map(({ label, val, cls }) => (
          <Card key={label} className="p-3.5 text-center sm:p-4">
            <div
              className={`font-display text-lg font-bold leading-none tabular sm:text-xl ${cls}`}
            >
              {val}
            </div>
            <div className="mt-1.5 text-xs font-medium text-slate-600">{label}</div>
          </Card>
        ))}
      </div>

      {/* Quick add */}
      <section aria-label="Quick add">
        <SectionTitle>Quick Add</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          {Object.entries(TYPE_META).map(([type, meta]) => (
            <button
              key={type}
              onClick={() => openForm(type)}
              className={`flex min-h-[72px] items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-transform active:scale-[0.97] ${meta.ring}`}
            >
              <span className="text-2xl sm:text-3xl" aria-hidden="true">{meta.icon}</span>
              <span className="min-w-0">
                <span className="block font-display text-sm font-semibold">Log {meta.label}</span>
                {/* No opacity here — 70% of amber-900 on amber-50 fails AA. */}
                <span className="block text-xs">Tap to add</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section aria-label="Filters" className="space-y-3">
        <label className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
            Month
          </span>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/15"
          />
        </label>

        <ChipRow>
          {["", "fuel", "food"].map((t) => (
            <Chip key={t} active={filterType === t} onClick={() => setFilterType(t)}>
              {t ? `${TYPE_META[t].icon} ${TYPE_META[t].label}` : "All"}
            </Chip>
          ))}
        </ChipRow>
      </section>

      {/* List */}
      <section aria-label="Expense list">
        {loading ? (
          <SkeletonList count={4} className="h-[84px] rounded-2xl" />
        ) : expenses.length === 0 ? (
          <EmptyState
            icon="💸"
            title="No expenses this month"
            hint={`Nothing logged for ${month}.`}
            action={<Button onClick={() => openForm(null)}>Log an expense</Button>}
          />
        ) : (
          <ul className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
            {expenses.map((exp) => {
              const meta = TYPE_META[exp.type] || TYPE_META.food;
              return (
                <li key={exp._id}>
                  <Card className="flex items-center gap-3 p-4">
                    <span
                      aria-hidden="true"
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border text-xl ${meta.ring}`}
                    >
                      {meta.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-sm font-semibold text-slate-900">{meta.label}</span>
                        {exp.siteId?.name && (
                          <span className="truncate text-xs text-slate-500">
                            · {exp.siteId.name}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-slate-500">
                        {formatDate(exp.date)}
                        {exp.description ? ` · ${exp.description}` : ""}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-display font-bold text-slate-900 tabular">
                        {formatMoney(exp.amount)}
                      </div>
                      <Badge tone={STATUS_TONE[exp.status] || "amber"} className="mt-1">
                        {exp.status}
                      </Badge>
                    </div>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Form sheet — overlays instead of replacing the page, so the list and
          totals stay put behind it. */}
      {showForm && (
        <Sheet
          title="New expense"
          subtitle="Submitted for supervisor approval"
          onClose={closeForm}
          footer={
            <div className="flex gap-3">
              <Button variant="secondary" size="lg" block onClick={closeForm} type="button">
                Cancel
              </Button>
              <Button type="submit" form="expense-form" size="lg" block loading={saving}>
                {saving ? "Submitting…" : "Submit"}
              </Button>
            </div>
          }
        >
          <ExpenseForm
            formId="expense-form"
            sites={sites}
            vehicles={vehicles}
            defaultType={defaultType || "fuel"}
            onSavingChange={setSaving}
            onSave={() => {
              closeForm();
              fetchExpenses();
            }}
          />
        </Sheet>
      )}
    </div>
  );
}
